import type { ResizeHandle } from '../types/resize-handle'
import type { ResizeParams } from '../types/resize-params'
import type { ResizeResult } from '../types/resize-result'
import type { RotationAngleParams } from '../types/rotation-angle-params'
import type { RotationAngleResult } from '../types/rotation-angle-result'

// Constants
export const DEFAULT_MIN_DIMENSION = 0

// JS Implementation functions
export const clampDimension = (value: number, minDimension: number = DEFAULT_MIN_DIMENSION) =>
  Math.max(minDimension, value)

export function computeResizeJS(params: ResizeParams): ResizeResult {
  const { direction, initial, dx, dy, minDimension = DEFAULT_MIN_DIMENSION } = params
  let { x, y } = initial
  const { width, height } = initial

  let nextWidth = width
  let nextHeight = height

  if (direction.includes('e')) {
    nextWidth = clampDimension(width + dx, minDimension)
  }

  if (direction.includes('s')) {
    nextHeight = clampDimension(height + dy, minDimension)
  }

  if (direction.includes('w')) {
    nextWidth = clampDimension(width - dx, minDimension)
    x = x + (width - nextWidth)
  }

  if (direction.includes('n')) {
    nextHeight = clampDimension(height - dy, minDimension)
    y = y + (height - nextHeight)
  }

  return { x, y, width: nextWidth, height: nextHeight }
}

export function isCornerHandle(direction: ResizeHandle): boolean {
  return (
    (direction.includes('n') || direction.includes('s')) &&
    (direction.includes('e') || direction.includes('w'))
  )
}

/**
 * Corner proportional resize: pick uniform scale from pointer deltas so a fast flick on one
 * axis (with noise on the other) cannot explode the thin dimension. Growing both axes uses
 * min(scaleW, scaleH); shrinking both uses max. Mixed grow/shrink clamps the growing axis
 * against max(..., 1) on the shrinking side.
 */
export function pickCornerUniformScale(
  scaleW: number,
  scaleH: number,
  deltaW: number,
  deltaH: number
): number {
  const growW = deltaW > 0
  const growH = deltaH > 0
  const shrinkW = deltaW < 0
  const shrinkH = deltaH < 0

  if (growW && growH) return Math.min(scaleW, scaleH)
  if (shrinkW && shrinkH) return Math.max(scaleW, scaleH)
  if (growW && shrinkH) return Math.min(scaleW, Math.max(scaleH, 1))
  if (shrinkW && growH) return Math.min(scaleH, Math.max(scaleW, 1))
  if (growW) return scaleW
  if (growH) return scaleH
  if (shrinkW) return scaleW
  if (shrinkH) return scaleH

  return 1
}

/** Smallest uniform scale that keeps each already-valid axis at or above `minDimension`. */
export function minUniformScaleForAspect(
  initialW: number,
  initialH: number,
  minDimension: number
): number {
  const scaleW = initialW >= minDimension ? minDimension / initialW : 0
  const scaleH = initialH >= minDimension ? minDimension / initialH : 0

  return Math.max(scaleW, scaleH)
}

/**
 * Uniform scale from a resize handle (corners pick driving axis from drag; edges use `axis`).
 */
export function computeResizeConstrainedAspectJS(
  params: ResizeParams,
  opts?: { axis?: 'width' | 'height' }
): ResizeResult {
  const { direction, initial, dx, dy, minDimension = DEFAULT_MIN_DIMENSION } = params

  const initialW = initial.width
  const initialH = initial.height

  // No meaningful aspect ratio at 0 (or non-finite); unconstrained resize grows from the handle.
  if (!Number.isFinite(initialW) || !Number.isFinite(initialH) || initialW <= 0 || initialH <= 0) {
    return computeResizeJS(params)
  }

  // Convert pointer delta into "positive means grow" deltas for W/H.
  const deltaW = direction.includes('e') ? dx : direction.includes('w') ? -dx : 0
  const deltaH = direction.includes('s') ? dy : direction.includes('n') ? -dy : 0

  const scaleW = (initialW + deltaW) / initialW
  const scaleH = (initialH + deltaH) / initialH
  const minScale = minUniformScaleForAspect(initialW, initialH, minDimension)

  let scale = isCornerHandle(direction)
    ? pickCornerUniformScale(scaleW, scaleH, deltaW, deltaH)
    : (opts?.axis ?? 'width') === 'width'
      ? scaleW
      : scaleH

  if (!Number.isFinite(scale)) scale = 1
  if (scale < minScale) scale = minScale

  const nextW = initialW * scale
  const nextH = initialH * scale

  // Corners: anchor the opposite corner. Edge-only: anchor the midpoint of the opposite edge
  // (so middle handles scale from center on that edge, e.g. top handle → bottom edge baseline).
  let nextX = initial.x
  let nextY = initial.y

  if (isCornerHandle(direction)) {
    if (direction.includes('w')) {
      nextX = initial.x + (initialW - nextW)
    }

    if (direction.includes('n')) {
      nextY = initial.y + (initialH - nextH)
    }
  } else {
    if (direction === 'n') {
      nextX = initial.x + (initialW - nextW) / 2
      nextY = initial.y + (initialH - nextH)
    } else if (direction === 's') {
      nextX = initial.x + (initialW - nextW) / 2
      nextY = initial.y
    } else if (direction === 'e') {
      nextX = initial.x
      nextY = initial.y + (initialH - nextH) / 2
    } else if (direction === 'w') {
      nextX = initial.x + (initialW - nextW)
      nextY = initial.y + (initialH - nextH) / 2
    }
  }

  return { x: nextX, y: nextY, width: nextW, height: nextH }
}

export function computeRotationAngleJS(params: RotationAngleParams): RotationAngleResult {
  const { centerX, centerY, mouseX, mouseY, startAngle, initialRotation = 0 } = params

  const angle = Math.atan2(mouseY - centerY, mouseX - centerX)
  const delta = startAngle !== undefined ? angle - startAngle : 0
  const degrees = (initialRotation + (delta * 180) / Math.PI + 360) % 360

  return { degrees }
}
